import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Loader2, Save, FileBox } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, authHeader, sendSermonToEditor } from "./shared";
import { SkeletonOutline, EmptyState } from "./_shared_ui";
import ExportMenu from "./ExportMenu";

export default function NotesToSermonPanel({ t, onSaved }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [outline, setOutline] = useState(null);

  const generate = async () => {
    if (!notes.trim() || notes.trim().length < 20) {
      toast.error(t("notes_too_short") || "Please write at least a short paragraph of notes");
      return;
    }
    setLoading(true);
    setOutline(null);
    try {
      const res = await axios.post(`${API}/api/pulpit/notes-to-sermon`, { notes }, { headers: authHeader() });
      setOutline(res.data.outline);
      toast.success(t("outline_generated") || "Outline generated");
    } catch (err) {
      toast.error(getErrorMessage(err, t("generation_failed") || "Generation failed"));
    } finally { setLoading(false); }
  };

  const saveOutline = async () => {
    if (!outline) return;
    try {
      await axios.post(`${API}/api/pulpit/sermons`, {
        title: outline.title || "Untitled Sermon",
        sermon_type: "notes",
        scripture: outline.scripture_main || null,
        content: outline,
      }, { headers: authHeader() });
      toast.success(t("sermon_saved") || "Saved to library");
      onSaved && onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, t("save_failed") || "Save failed"));
    }
  };

  const sendToEditor = () => {
    if (!outline) return;
    sendSermonToEditor({
      title: outline.title || "Sermon",
      sermon_type: "notes", content: outline, navigate, t,
    });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-base">{t("notes_to_sermon") || "Notes to Sermon"}</CardTitle>
          <CardDescription>{t("notes_desc") || "Paste raw notes; AI organizes them into a polished outline"}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={t("notes_placeholder") || "Paste your raw notes, bullet points, or stream-of-consciousness here..."}
            className="min-h-[400px]" />
          <Button onClick={generate} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="generate-from-notes-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {loading ? (t("generating") || "Generating...") : (t("structure_my_notes") || "Structure My Notes")}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{outline?.title || (t("result") || "Result")}</CardTitle>
            <CardDescription>{outline?.scripture_main || (t("no_outline_yet") || "No outline yet")}</CardDescription>
          </div>
          {outline && (
            <div className="flex gap-1">
              <ExportMenu title={outline.title || "Sermon"} sermonType="notes" content={outline} t={t} testId="export-notes" />
              <Button variant="outline" size="sm" onClick={sendToEditor} data-testid="send-notes-to-editor-btn" className="text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10">
                <FileBox className="w-3.5 h-3.5 mr-1" />
                {t("send_to_editor") || "Send to Editor"}
              </Button>
              <Button variant="outline" size="sm" onClick={saveOutline} data-testid="save-notes-outline-btn">
                <Save className="w-3.5 h-3.5 mr-1" />
                {t("save") || "Save"}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-2">
            {loading ? (
              <SkeletonOutline />
            ) : !outline ? (
              <EmptyState
                icon={FileText}
                title={t("notes_empty_title") || "Paste your raw notes"}
                description={t("notes_empty_desc") || "Bullet points, stream of consciousness, or scribbled sentences — AI will structure them into a clean outline."}
                testId="notes-empty"
              />
            ) : (
              <div className="space-y-3 text-sm" data-testid="notes-outline-result">
                <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                  <span className="text-xs uppercase text-amber-400">{t("big_idea") || "Big Idea"}</span>
                  <p className="mt-1">{outline.big_idea}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-300 text-xs uppercase">{t("introduction") || "Introduction"}</h4>
                  <p className="mt-1 text-slate-200">{outline.introduction}</p>
                </div>
                {(outline.points || []).map((pt, idx) => (
                  <div key={idx} className="border-l-2 border-amber-500/40 pl-3">
                    <h4 className="font-semibold">{idx + 1}. {pt.heading}</h4>
                    {pt.scripture && <p className="text-xs text-amber-300">{pt.scripture}</p>}
                    <p className="mt-1 text-slate-200">{pt.explanation}</p>
                  </div>
                ))}
                <div>
                  <h4 className="font-semibold text-slate-300 text-xs uppercase">{t("application") || "Application"}</h4>
                  <p className="mt-1 text-slate-200">{outline.application}</p>
                </div>
                <div className="p-2 rounded bg-slate-800/60">
                  <span className="text-xs uppercase text-slate-400">{t("call_to_action") || "Call to Action"}</span>
                  <p className="mt-1 text-slate-100">{outline.call_to_action}</p>
                </div>
                {outline.preserved_quotes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-300 text-xs uppercase">{t("your_words") || "Preserved From Your Notes"}</h4>
                    <ul className="mt-1 list-disc list-inside text-xs text-slate-400 space-y-0.5">
                      {outline.preserved_quotes.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
